import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLoadScript } from '@react-google-maps/api';
import { env } from '@/core/config/env';
import { getCounties, getStates } from '@/shared/api/geographyApi';
import type { SearchFilters } from '../types';
import { useAllTaxonomy } from './useTaxonomy';
import {
  hasUrlFilters,
  parseUrlFilters,
  resolveUrlTaxonomy,
  writeUrlFilters,
} from '../utils/providerSearchUrlFilters';
import { resolveUrlLocation } from '../utils/resolveUrlLocation';
import { GOOGLE_MAPS_LIBRARIES, geocodeUsLocation } from '../utils/geocodedLocation';

type ResolvedLocation = Awaited<ReturnType<typeof resolveUrlLocation>>;

/**
 * Reads provider search filters from the URL on entry.
 *
 * Returns `filters: null` when the URL has no supported filter params. Otherwise
 * it validates every id against the taxonomy, resolves the location (by id or
 * by county/city/state name; a city goes through the same Google geocoding as
 * the location box) and drops whatever doesn't resolve. A failed lookup only
 * drops that filter; it never blocks the search. Writing back to the URL is
 * {@link useSyncUrlSearchFilters}'s job.
 *
 * ponytail: params are read on mount only. A same-page navigation to another
 * filtered link (component stays mounted) is not re-read; key the page on the
 * location if that ever becomes a real flow.
 */
export const useUrlSearchFilters = (): {
  isResolving: boolean;
  filters: Partial<SearchFilters> | null;
} => {
  const [searchParams] = useSearchParams();
  const [parsed] = useState(() =>
    hasUrlFilters(searchParams) ? parseUrlFilters(searchParams) : null,
  );
  const [location, setLocation] = useState<ResolvedLocation | null>(null);
  const [filters, setFilters] = useState<Partial<SearchFilters> | null>(null);
  const locationStarted = useRef(false);
  const { categories, specialties, isLoading: isTaxonomyLoading } = useAllTaxonomy();
  // Same id/config as LocationSearch, so the script is only loaded once per page.
  const { isLoaded: isGoogleLoaded, loadError: googleLoadError } = useLoadScript({
    googleMapsApiKey: env.GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const query = parsed?.location;
  const isGoogleSettled = isGoogleLoaded || !!googleLoadError;

  useEffect(() => {
    if (!parsed || locationStarted.current) return;
    if (query?.city && !isGoogleSettled) return;
    locationStarted.current = true;
    resolveUrlLocation(query ?? {}, {
      getStates,
      getCounties,
      geocodeCity: isGoogleLoaded ? geocodeUsLocation : async () => null,
    }).then(setLocation, () => setLocation({}));
  }, [parsed, query, isGoogleSettled, isGoogleLoaded]);

  // Only links with taxonomy ids need the taxonomy loaded to validate them.
  const needsTaxonomy = !!(parsed?.categoryIds || parsed?.specialtyIds);

  useEffect(() => {
    if (!parsed || filters || !location || (needsTaxonomy && isTaxonomyLoading)) return;
    setFilters({ ...resolveUrlTaxonomy(parsed, { categories, specialties }), ...location });
  }, [parsed, filters, location, needsTaxonomy, isTaxonomyLoading, categories, specialties]);

  if (!parsed) return { isResolving: false, filters: null };
  return { isResolving: !filters, filters };
};

/**
 * Keeps the URL equal to the current filters, so the address bar is always a
 * shareable link to this search. Uses `replace`: filter changes don't add
 * history entries. Since the saved filters change together with the URL, a
 * reload reads back the same search it shows.
 */
export const useSyncUrlSearchFilters = (filters: SearchFilters): void => {
  const [searchParams, setSearchParams] = useSearchParams();
  const current = searchParams.toString();
  const next = writeUrlFilters(searchParams, filters).toString();

  useEffect(() => {
    if (next !== current) setSearchParams(next, { replace: true });
  }, [next, current, setSearchParams]);
};
